<template>
  <!-- 运维诊断日报侧边栏抽屉 -->
  <Teleport to="body">
    <el-drawer v-model="isActive" class="alarm-report-drawer" :class="{ 'is-detail-view': drawerView !== 'report' }"
      size="55%" :append-to-body="true">
      <template #header>
        <div class="drawer-page-title">
          <v-btn v-if="drawerView !== 'report'" icon="mdi-arrow-left" variant="text" density="compact" aria-label="返回日报"
            @click="backToReport" />
          <div>
            <div class="drawer-title">{{ drawerTitle }}</div>
          </div>
          <div v-if="drawerView === 'report'" class="drawer-meta">
            <!-- <span class="drawer-meta-date">{{ reportDate }}</span>
            <span class="drawer-meta-divider"></span> -->
            <span class="drawer-meta-range">数据时间 {{ reportTimeRange }}</span>
          </div>
          <div v-else-if="drawerView === 'alarm-code-list'" class="drawer-meta">
            <span class="drawer-meta-range">{{ reportTimeRange }}</span>
            <span class="drawer-meta-divider"></span>
            <span class="drawer-meta-range">共 {{ alarmCodeTotal }} 条</span>
          </div>
        </div>
      </template>

      <template v-if="drawerView === 'report'">
        <div class="report-content">
          <OverviewCards :cards="summaryCards" :station-total="stationTotalCount" :recovered-count="recoveredCount" />

          <SuggestionList :suggestions="suggestions" @view="onSuggestionView" />

          <AlarmDetailTable ref="alarmDetailTableRef" v-model:priority-filter="priorityFilter"
            v-model:search="alarmSearch" :groups="filteredStationGroups" :expanded-stations="expandedStations"
            :highlighted-stations="highlightedStations" :station-ids-filter="stationIdsFilter"
            :default-priority="defaultPriority"
            :set-station-ref="setStationRef" :loading="stationsLoading" :page="stationPage"
            :page-size="STATION_PAGE_SIZE" :total="stationListTotal" @toggle-station="toggleStation"
            @page-change="onStationPageChange" @open-analysis="openStationAnalysis"
            @clear-station-filter="clearStationFilter" @priority-change="onPriorityChange" />

          <ClusterAnalysis :cluster-summary="clusterSummary" :cluster-list="clusterList"
            @open-code="openAlarmCodeList" />
        </div>
      </template>

      <StationAlarmAnalysisDialog v-else-if="drawerView === 'station-analysis'" :report-id="reportMeta?.reportId"
        :station-id="analysisStationId" :station-name="analysisStationName" :station-level="analysisStationLevel"
        :alarms="stationAlarmsForAnalysis" />

      <AlarmCodeListDialog v-else :alarm-code="selectedAlarmCode" :time-range="reportTimeRange"
        :records="alarmCodeRecords" :total="alarmCodeTotal" :page="alarmCodePage" :state-filter="alarmCodeState"
        :page-size="ALARM_CODE_PAGE_SIZE" :loading="codeRecordsLoading" @change="onAlarmCodeChange" />
    </el-drawer>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import StationAlarmAnalysisDialog from './StationAlarmAnalysisDialog.vue'
import OverviewCards from './AlarmReportDrawer/OverviewCards.vue'
import SuggestionList from './AlarmReportDrawer/SuggestionList.vue'
import AlarmDetailTable from './AlarmReportDrawer/AlarmDetailTable.vue'
import ClusterAnalysis from './AlarmReportDrawer/ClusterAnalysis.vue'
import AlarmCodeListDialog from './AlarmReportDrawer/AlarmCodeListDialog.vue'
import { LEVEL_ORDER } from './AlarmReportDrawer/stationLevel'
import {
  getDailyReport,
  getDailyReportStations,
  getStationAlarms,
  getDailyReportClusters,
  getDailyReportAlarms
} from '@/api/alarmDailyReport.js'

const isActive = defineModel({
  default: false
})

const props = defineProps({
  reportId: {
    type: [String, Number],
    default: ''
  }
})

const reportDate = computed(() => reportMeta.value?.reportDate || '')
const reportTimeRange = computed(() => {
  const startTime = reportMeta.value?.dataStartTime
  const endTime = reportMeta.value?.dataEndTime
  return startTime && endTime ? `${startTime} ~ ${endTime}` : ''
})

// 告警状态枚举映射：接口返回英文 UNRECOVERED/RECOVERED/AUTO_RECOVERED，
// 子组件依赖中文「未恢复/已恢复/已自动恢复」，在数据进入子组件前统一转换。
const ALARM_STATE_LABEL = {
  UNRECOVERED: '未恢复',
  RECOVERED: '已恢复',
  AUTO_RECOVERED: '已自动恢复',
}
const toStateLabel = (state) => ALARM_STATE_LABEL[state] ?? state ?? ''

// —— 数据源（全部来自接口）——
// 运维诊断日报基础信息（latest 返回的 Report 对象，null 表示无 READY 日报）
const reportMeta = ref(null)
// 电站聚合 rows（stations 接口返回的 rows）
const stationRows = ref([])
const stationTotalCount = ref(0)
const stationListTotal = ref(0)
const STATION_PAGE_SIZE = 5
const stationPage = ref(1)
// 聚类数据（clusters 接口返回的 data：{ summary, items, ... }）
const clusterData = ref(null)
// 站内告警缓存：stationId → { rows, total, loaded }，懒加载（展开电站行时才请求）
const stationAlarmsMap = ref({})
// 编码下钻记录（alarms 接口返回的 items 适配后，仅当前页）
const alarmCodeRecords = ref([])
// 编码下钻后端分页状态
const ALARM_CODE_PAGE_SIZE = 15
const alarmCodePage = ref(1)
const alarmCodeState = ref('all') // all | UNRECOVERED | RECOVERED | AUTO_RECOVERED
const alarmCodeTotal = ref(0)

// 加载态
const stationsLoading = ref(false)
const clustersLoading = ref(false)
const codeRecordsLoading = ref(false)
let stationsRequestId = 0
// 轮询句柄
let statusTimer = null
const clearStatusTimer = () => {
  if (statusTimer) { clearTimeout(statusTimer); statusTimer = null }
}

// 筛选和搜索（双向绑定给 AlarmDetailTable）
const priorityFilter = ref('P0')
const defaultPriority = ref('P0')
const alarmSearch = ref('')
const stationIdsFilter = ref([])
const alarmDetailTableRef = ref(null)
let resettingStationFilters = false
let searchDebounce = null

// 电站行展开状态（多开）+ 建议「查看这N站」高亮的电站
const expandedStations = ref([])
const highlightedStations = ref([])

// 电站行 DOM 引用，用于「查看这N站」时定位滚动；以普通函数形式下发给 AlarmDetailTable 供其 :ref 回调使用
const stationRefs = {}
function setStationRef(stationId, el) {
  if (el) stationRefs[stationId] = el
  else delete stationRefs[stationId]
}

// 建议「查看这N站」：清筛选、展开并高亮滚动到目标电站
function locateStations(stationIds) {
  priorityFilter.value = 'all'
  alarmSearch.value = ''
  expandedStations.value = [...new Set([...expandedStations.value, ...stationIds])]
  highlightedStations.value = stationIds
  nextTick(() => {
    const el = stationRefs[stationIds[0]]
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
  setTimeout(() => { highlightedStations.value = [] }, 2400)
}

const drawerView = ref('report')
const analysisStationId = ref('')
const analysisStationName = ref('')
const analysisStationLevel = ref('')
const selectedAlarmCode = ref('')

const drawerTitle = computed(() => {
  if (drawerView.value === 'station-analysis') return '电站智能分析'
  if (drawerView.value === 'alarm-code-list') return `${selectedAlarmCode.value || '告警编码'} 告警列表`
  return '运维诊断日报'
})
const drawerSubtitle = computed(() => drawerView.value === 'alarm-code-list'
  ? `${reportTimeRange.value} · 共 ${alarmCodeRecords.value.length} 条`
  : '')

function backToReport() {
  drawerView.value = 'report'
}

// 打开电站智能分析
function openStationAnalysis(group) {
  analysisStationId.value = group.stationId
  analysisStationName.value = group.stationName
  // level 用接口下发的
  analysisStationLevel.value = group.level || ''
  drawerView.value = 'station-analysis'
  // 确保该站告警已加载（懒加载缓存，未展开电站行时也能拿到基本信息/告警详情数据）
  loadStationAlarms(group.stationId)
}

function inferEquipmentType(equipmentName = '') {
  const name = equipmentName.toUpperCase()
  if (name.includes('PCS')) return 'PCS'
  if (name.includes('BCS')) return 'BCS'
  if (name.includes('BMS')) return 'BMS'
  if (equipmentName.includes('空调')) return '空调'
  if (equipmentName.includes('电表')) return '电表'
  return '设备'
}

// 把站内告警 row（含 category 信息）适配为子组件消费的 alarm 形状（state 转中文）
// 新接口结构：row = 设备维度，含 categories: [{ category, alarms: [...] }]
function normalizeStationAlarm(alarm, equipmentRow, category) {
  return {
    id: alarm.alarmId ?? '',
    category: category || 'NORMAL',
    alarmCode: alarm.alarmCode || '',
    faultCode: alarm.faultCode || '',
    content: alarm.content || '',
    state: toStateLabel(alarm.state),
    triggerCount: alarm.triggerCount ?? 1,
    recoverCount: alarm.recoverCount,
    firstTriggerTime: alarm.firstTriggerTime || '',
    lastTriggerTime: alarm.lastTriggerTime || '',
    recoveryTime: alarm.recoveryTime || '',
    value: alarm.value,
    remark: alarm.remark || '',
    suggestion: alarm.suggestion || '',
    equipmentId: equipmentRow.equipmentId || '',
    equipmentName: equipmentRow.equipmentName || '',
    equipmentUuid: equipmentRow.equipmentUuid || '',
    equipmentType: equipmentRow.equipmentType || inferEquipmentType(equipmentRow.equipmentName),
  }
}

function openAlarmCodeList(item) {
  selectedAlarmCode.value = item.alarmCode
  alarmCodePage.value = 1
  alarmCodeState.value = 'all'
  drawerView.value = 'alarm-code-list'
  loadAlarmCodeRecords()
}

// 编码下钻弹窗内翻页 / 切换状态：更新分页状态后重新请求
function onAlarmCodeChange({ page, state }) {
  alarmCodePage.value = page
  alarmCodeState.value = state
  loadAlarmCodeRecords()
}

// 编码下钻：调用 alarms 接口（后端分页），适配为 AlarmRecordTable 消费的行形状
async function loadAlarmCodeRecords() {
  const alarmCode = selectedAlarmCode.value
  if (!reportMeta.value?.reportId || !alarmCode) return
  codeRecordsLoading.value = true
  try {
    const res = await getDailyReportAlarms({
      reportId: reportMeta.value.reportId,
      alarmCode,
      state: alarmCodeState.value === 'all' ? undefined : alarmCodeState.value,
      pageNum: alarmCodePage.value,
      pageSize: ALARM_CODE_PAGE_SIZE
    })
    // R<AlarmPage>：业务数据在 res.data，items 在 res.data.items，总数在 header.total
    const data = res?.data || {}
    const items = data.items || []
    alarmCodeTotal.value = data.header?.total ?? 0
    alarmCodeRecords.value = items.map(item => ({
      rowId: `${item.id}-${item.stationId}`,
      id: item.id,
      stationId: item.stationId,
      stationName: item.stationName || '',
      equipmentId: item.equipmentId || '',
      equipmentName: item.equipmentName || '',
      equipmentUuid: item.equipmentUuid || '',
      equipmentType: item.equipmentType || '',
      alarmCode: item.alarmCode || '',
      faultCode: item.faultCode || '',
      category: item.category || 'NORMAL',
      content: item.alarmDescription || item.content || '',
      state: toStateLabel(item.alarmState),
      firstTriggerTime: item.alarmStartTime || '',
      lastTriggerTime: item.lastTriggerTime || '',
      recoveryTime: item.recoveryTime || '',
      triggerCount: item.triggerCount,
      recoverCount: item.recoveryCount,
      value: item.value,
      remark: item.remark || '',
      suggestion: item.suggestion || '',
    }))
  } finally {
    codeRecordsLoading.value = false
  }
}

// 整站分析页面的告警详情 Tab 继续使用日报站内告警；Agent 上下文由专用接口提供。
const stationAlarmsForAnalysis = computed(() => {
  if (!analysisStationId.value) return []
  const entry = stationAlarmsMap.value[analysisStationId.value]
  if (!entry?.rows) return []
  // 新接口 rows 是设备维度，展平 categories.alarms 为告警列表
  return groupStationAlarmsByDevice(entry.rows).flatMap(device => device.alarms)
})

// 概览卡：从后端 overview 派生（不再前端 reduce）
// 每张卡主数值与副标注「涉及 N 站」均取自接口 overview
const OVERVIEW = computed(() => reportMeta.value?.overview || null)
const recoveredCount = computed(() => OVERVIEW.value?.autoRecoveredCount ?? 0)

const summaryCards = computed(() => {
  const o = OVERVIEW.value
  return [
    { key: 'total', label: '告警总数', cls: 'kpi-total', count: o?.sourceAlarmRecordCount ?? 0, stationCount: o?.totalTriggerStationCount ?? 0 },
    { key: 'safety', label: '影响安全', cls: 'kpi-red', count: o?.safetyUnrecoveredCount ?? 0, stationCount: o?.safetyUnrecoveredStationCount ?? 0 },
    { key: 'income', label: '影响收益', cls: 'kpi-orange', count: o?.incomeUnrecoveredCount ?? 0, stationCount: o?.incomeUnrecoveredStationCount ?? 0 },
    { key: 'unrecovered', label: '未恢复', cls: 'kpi-status', count: o?.unrecoveredCount ?? 0, stationCount: o?.unrecoveredStationCount ?? 0 },
  ]
})

// 计算属性：按电站聚合的告警明细
// 电站行来自 stations 接口（level 由接口下发，不再前端推导）；展开时从 stationAlarmsMap 取站内告警并按设备分组。
// keyword / level 筛选改为重新请求接口（见 loadStations），此处只负责把已加载的 stationRows 组装成组件 shape。
const filteredStationGroups = computed(() => {
  return stationRows.value.map(group => {
    const entry = stationAlarmsMap.value[group.stationId]
    // 站内告警按设备分组（仅展开后才有数据）
    const devices = entry?.rows?.length
      ? groupStationAlarmsByDevice(entry.rows)
      : []
    return {
      stationId: group.stationId,
      stationName: group.stationName,
      level: group.level,
      // 接口已基于全部未恢复告警计算，直接用
      categoryStats: group.categoryStats || { safety: 0, income: 0, normal: 0 },
      totalAlarmCount: group.totalTriggerCount ?? 0,
      alarmCount: group.unrecoveredCount ?? 0,
      unrecoveredCount: group.unrecoveredCount ?? 0,
      deviceCount: group.deviceCount ?? 0,
      latestTriggerTime: group.latestUnrecoveredTime || '',
      devices,
      loaded: !!entry?.loaded,
      loading: !!entry?.loading,
    }
  }).sort((a, b) => {
    const pa = LEVEL_ORDER[a.level] ?? 99
    const pb = LEVEL_ORDER[b.level] ?? 99
    if (pa !== pb) return pa - pb
    if (b.unrecoveredCount !== a.unrecoveredCount) return b.unrecoveredCount - a.unrecoveredCount
    return b.totalAlarmCount - a.totalAlarmCount
  })
})

// 站内告警 rows 已是设备维度（新接口结构）：每个 row 含 categories: [{ category, alarms }]
// 展平为子组件消费的 devices: [{ equipmentId, ..., totalTriggerCount, unrecoveredCount, alarms }]
function groupStationAlarmsByDevice(rows) {
  return rows.map(row => ({
    equipmentId: row.equipmentId || '',
    equipmentName: row.equipmentName || '',
    equipmentUuid: row.equipmentUuid || '',
    equipmentType: row.equipmentType || inferEquipmentType(row.equipmentName),
    // 设备级统计直接用接口字段，不前端累加
    totalTriggerCount: row.totalTriggerCount ?? 0,
    unrecoveredCount: row.unrecoveredCount ?? 0,
    alarms: (row.categories || []).flatMap(cat =>
      (cat.alarms || []).map(alarm => normalizeStationAlarm(alarm, row, cat.category))
    ),
  }))
}

// 高频聚类：消费 clusters 接口返回的 data，不再前端 reduce
const clusterList = computed(() => {
  const items = clusterData.value?.items || []
  return items.map(item => ({
    alarmCode: item.alarmCode || '',
    content: item.content || '',
    category: item.category || 'NORMAL',
    count: item.triggerCount ?? 0,
    stationCount: item.stationCount ?? 0,
    unrecoveredCount: item.unrecoveredCount ?? 0,
    trend: item.changeRate ?? 0,
    insight: item.insight || '当前未发现明显异常变化，建议持续观察。',
    diagnosisSteps: Array.isArray(item.diagnosisSteps) ? item.diagnosisSteps : [],
  }))
})

// 聚类总体结论：直接用后端 summary.text
const clusterSummary = computed(() => clusterData.value?.summary?.text || '')

// 处置建议：消费后端 suggestions，保留电站信息供建议展示和明细筛选。
const suggestions = computed(() => {
  const list = reportMeta.value?.suggestions || []
  return list.map(s => ({
    id: `sug-${s.alarmCode}`,
    category: s.category || 'NORMAL',
    alarmContent: s.content || '',
    impact: s.suggestion || '',
    stations: (s.stations || []).map(station => ({
      stationId: station.stationId,
      stationName: station.stationName || '',
    })),
    stationCount: s.stationCount ?? 0,
    unrecoveredCount: s.unrecoveredCount ?? 0,
  }))
})

// 导出报告
function exportReport() {
  console.log('导出报告')
  // TODO: 实现导出功能
}

// 转工单/派单相关按钮当前已隐藏，保留 TODO 供后续恢复时参考

// —— 数据加载 ——
async function loadReport(reportId) {
  if (!reportId) return
  try {
    const res = await getDailyReport(reportId)
    // @/utils/request 拦截器返回 { code, msg, data }，业务数据在 data
    const report = res?.data ?? null
    reportMeta.value = report
    if (report?.status === 'READY') {
      stationTotalCount.value = report.stationTotal ?? 0
      loadStations({ fallbackToP1: true })
      loadClusters()
    } else if (report && report.status === 'GENERATING') {
      startStatusPolling(report.reportId)
    }
  } catch {
    // 请求失败由 request 拦截器统一 toast，此处静默
  }
}

async function loadStations({ fallbackToP1 = false } = {}) {
  if (!reportMeta.value?.reportId) return
  const requestId = ++stationsRequestId
  stationsLoading.value = true
  const searchValue = String(alarmSearch.value || '').trim()
  const isStationIdSearch = /^\d+$/.test(searchValue)
  try {
    const res = await getDailyReportStations({
      reportId: reportMeta.value.reportId,
      level: priorityFilter.value === 'all' ? undefined : priorityFilter.value,
      keyword: searchValue && !isStationIdSearch ? searchValue : undefined,
      stationIds: isStationIdSearch
        ? [searchValue]
        : (stationIdsFilter.value.length ? stationIdsFilter.value : undefined),
      pageNum: stationPage.value,
      pageSize: STATION_PAGE_SIZE
    })
    if (requestId !== stationsRequestId) return
    const rows = res?.rows || []
    const total = Number(res?.total ?? 0)
    stationRows.value = rows
    stationListTotal.value = total
    if (fallbackToP1 && priorityFilter.value === 'P0' && total === 0) {
      defaultPriority.value = 'P1'
      priorityFilter.value = 'P1'
      return
    }
  } finally {
    if (requestId === stationsRequestId) stationsLoading.value = false
  }
}

async function loadStationAlarms(stationId) {
  if (!reportMeta.value?.reportId || !stationId) return
  const currentEntry = stationAlarmsMap.value[stationId]
  if (currentEntry?.loaded || currentEntry?.loading) return
  stationAlarmsMap.value = {
    ...stationAlarmsMap.value,
    [stationId]: { ...currentEntry, loading: true }
  }
  try {
    const res = await getStationAlarms({
      reportId: reportMeta.value.reportId,
      stationId,
      pageNum: 1,
      pageSize: 100
    })
    stationAlarmsMap.value = {
      ...stationAlarmsMap.value,
      [stationId]: { rows: res?.rows || [], total: res?.total ?? 0, loaded: true, loading: false }
    }
  } finally {
    const latestEntry = stationAlarmsMap.value[stationId]
    if (latestEntry?.loading) {
      stationAlarmsMap.value = {
        ...stationAlarmsMap.value,
        [stationId]: { ...latestEntry, loading: false }
      }
    }
  }
}

async function loadClusters() {
  if (!reportMeta.value?.reportId) return
  clustersLoading.value = true
  try {
    const res = await getDailyReportClusters({
      reportId: reportMeta.value.reportId,
      pageNum: 1,
      pageSize: 100
    })
    // R<ClusterPage>：业务数据在 res.data
    clusterData.value = res?.data ?? null
  } finally {
    clustersLoading.value = false
  }
}

// 状态轮询：GENERATING 时每 2.5s 查指定运维诊断日报，到达 READY 重载、FAILED 展示 errorMessage
function startStatusPolling(reportId) {
  clearStatusTimer()
  statusTimer = setTimeout(async () => {
    try {
      const res = await getDailyReport(reportId)
      const report = res?.data ?? null
      if (report?.status === 'READY') {
        reportMeta.value = report
        stationTotalCount.value = report.stationTotal ?? 0
        loadStations({ fallbackToP1: true })
        loadClusters()
      } else if (report?.status === 'FAILED') {
        reportMeta.value = report
      } else {
        startStatusPolling(reportId)
      }
    } catch {
      startStatusPolling(reportId)
    }
  }, 2500)
}

// 展开/收起电站行：展开时懒加载站内告警
function toggleStation(stationId) {
  const idx = expandedStations.value.indexOf(stationId)
  if (idx >= 0) expandedStations.value.splice(idx, 1)
  else {
    expandedStations.value.push(stationId)
    loadStationAlarms(stationId)
  }
}

// 处置建议：按建议关联的电站筛选告警明细，不叠加旧的等级或关键词条件。
function onSuggestionView(item) {
  const stationIds = (item.stations || [])
    .map(station => station.stationId)
    .filter(stationId => stationId !== null && stationId !== undefined)
  if (!stationIds.length) return
  priorityFilter.value = 'all'
  alarmSearch.value = ''
  stationIdsFilter.value = stationIds
  nextTick(() => alarmDetailTableRef.value?.scrollIntoView())
}

function onPriorityChange() {
  if (stationIdsFilter.value.length) stationIdsFilter.value = []
  highlightedStations.value = []
}

async function clearStationFilter() {
  clearTimeout(searchDebounce)
  resettingStationFilters = true
  defaultPriority.value = 'P0'
  priorityFilter.value = 'P0'
  alarmSearch.value = ''
  stationIdsFilter.value = []
  stationPage.value = 1
  await nextTick()
  resettingStationFilters = false
  if (!isActive.value || !reportMeta.value) return
  loadStations({ fallbackToP1: true })
}

function onStationPageChange(page) {
  const nextPage = Number(page) || 1
  if (nextPage === stationPage.value) return
  stationPage.value = nextPage
  expandedStations.value = []
  highlightedStations.value = []
  loadStations()
}

// 监听打开事件，加载当前待办关联的运维诊断日报
watch(() => isActive.value, (newVal) => {
  if (newVal) {
    drawerView.value = 'report'
    stationsRequestId += 1
    stationAlarmsMap.value = {}
    expandedStations.value = []
    highlightedStations.value = []
    stationPage.value = 1
    loadReport(props.reportId)
  } else {
    clearStatusTimer()
    clearTimeout(searchDebounce)
    stationsRequestId += 1
    defaultPriority.value = 'P0'
    priorityFilter.value = 'P0'
    alarmSearch.value = ''
    stationIdsFilter.value = []
    stationPage.value = 1
  }
})

// 筛选/搜索变化时重新请求电站列表
watch([priorityFilter, stationIdsFilter], () => {
  if (resettingStationFilters || !isActive.value || !reportMeta.value) return
  stationPage.value = 1
  loadStations()
})
watch(alarmSearch, (val) => {
  if (resettingStationFilters || !isActive.value || !reportMeta.value) return
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    stationPage.value = 1
    loadStations()
  }, 300)
})

</script>

<style lang="scss">
.alarm-report-drawer {
  --report-text: #2e2e2e;
  --report-muted: rgba(46, 46, 46, 0.6);
  --report-border: rgba(0, 0, 0, 0.06);
  --report-surface: transparent;
  font-family: 'PingFang SC', 'HarmonyOS Sans SC', sans-serif;

  .el-drawer__header {
    margin-bottom: 0;
    padding: 20px 24px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--report-border);
  }

  .el-drawer__body {
    padding: 0 !important;
    overflow-y: auto;
    background: transparent !important;
  }

  &.is-detail-view .el-drawer__close-btn {
    display: none;
  }
}
</style>

<style scoped lang="scss">
.drawer-page-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.drawer-title {
  color: #2e2e2e;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
}

.drawer-subtitle {
  margin-top: 4px;
  color: rgba(46, 46, 46, 0.6);
  font-size: 13px;
}

.drawer-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 16px;
  font-size: 13px;
  color: rgba(46, 46, 46, 0.6);
  white-space: nowrap;
}

.drawer-meta-date {
  color: #2e2e2e;
  font-weight: 500;
  font-size: 14px;
}

.drawer-meta-divider {
  width: 1px;
  height: 14px;
  background: rgba(0, 0, 0, 0.12);
}

.report-content {
  padding: 0 24px 40px;
}
</style>
